import { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  TrendingUp, 
  DollarSign, 
  Receipt, 
  BarChart3, 
  Users, 
  LogOut,
  Menu,
  X,
  Home as HomeIcon,
  Settings,
  User,
  ChevronDown,
  Shield,
  Key,
  AlertCircle,
  LogIn,
  BookOpen,
  Car
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BASE_URL as API } from '../App';
import Home from './Home';
import CustomerList from './CustomerList';
import IncomePanel from './IncomePanel';
import ExpensePanel from './ExpensePanel';
import SummaryPanel from './SummaryPanel';
import UserManagement from './UserManagement';
import AccountSettings from './AccountSettings';
import Charts from './Charts';
import Checkbook from './Checkbook';
import MileageTracker from './MileageTracker';
import AdminDashboard from './AdminDashboard';
import Footer from './Footer';
import ScheduleCBackground from './ScheduleCBackground';

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Build menu items based on user role
  const menuItems = [];

  if (user?.role === 'super_admin') {
    // SuperAdmin menu - Admin first, no Home
    menuItems.push(
      { path: '/dashboard/admin', label: 'Admin', icon: Shield },
      { path: '/dashboard/income', label: 'Income', icon: DollarSign },
      { path: '/dashboard/expenses', label: 'Expenses', icon: Receipt },
      { path: '/dashboard/checkbook', label: 'Checkbook', icon: BookOpen },
      { path: '/dashboard/mileage', label: 'Mileage', icon: Car },
      { path: '/dashboard/summary', label: 'Summary & Tax', icon: BarChart3 },
      { path: '/dashboard/analytics', label: 'Analytics', icon: TrendingUp }
    );
  } else {
    // Regular users menu - Home first
    menuItems.push(
      { path: '/dashboard', label: 'Home', icon: HomeIcon },
      { path: '/dashboard/customers', label: 'Customers', icon: Users },
      { path: '/dashboard/income', label: 'Income', icon: DollarSign },
      { path: '/dashboard/expenses', label: 'Expenses', icon: Receipt },
      { path: '/dashboard/checkbook', label: 'Checkbook', icon: BookOpen },
      { path: '/dashboard/mileage', label: 'Mileage', icon: Car },
      { path: '/dashboard/summary', label: 'Summary & Tax', icon: BarChart3 },
      { path: '/dashboard/analytics', label: 'Analytics', icon: TrendingUp }
    );
    
    if (user?.role === 'owner') {
      menuItems.push({ path: '/dashboard/users', label: 'Managers', icon: Users });
    }
  }

  const handleLogout = async () => {
    // Log the logout activity
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.log('Logout logging failed:', error);
    }
    
    // Clear ALL caches and service workers to prevent old version issues
    try {
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          console.log('Service worker unregistered');
        }
      }
      
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          await caches.delete(cacheName);
          console.log('Cache deleted:', cacheName);
        }
      }
    } catch (error) {
      console.log('Cache clearing failed:', error);
    }
    
    onLogout();
    navigate('/login');
  };

  const handleStopImpersonating = () => {
    const originalToken = localStorage.getItem('originalToken');
    const originalEmail = localStorage.getItem('originalAdminEmail');
    
    if (originalToken) {
      // Restore original token
      localStorage.setItem('token', originalToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;
      
      // Clear impersonation flags
      localStorage.removeItem('originalToken');
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('originalAdminEmail');
      
      toast.success(`Returned to ${originalEmail}`);
      
      // Reload to refresh with original user
      window.location.href = '/dashboard/admin';
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/auth/reset-password`,
        {
          email: user.email,
          old_password: passwordData.oldPassword,
          new_password: passwordData.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Password changed successfully!');
      setShowChangePasswordDialog(false);
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950">
      {/* Top Bar */}
      <header className="bg-slate-900/80 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 lg:px-6 h-16">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-slate-300 hover:bg-slate-800"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">RevTracker</h1>
                <p className="text-xs text-slate-500">Financial Management</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 hover:bg-slate-800 text-slate-300" data-testid="user-menu-trigger">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-white" data-testid="user-email">{user?.email}</p>
                    <div className="flex gap-2 justify-end items-center">
                      <p className="text-xs text-slate-400 capitalize" data-testid="user-role">{user?.role}</p>
                      {user?.employment_type && (
                        <>
                          <span className="text-xs text-slate-600">•</span>
                          <p className="text-xs text-cyan-400 font-medium" data-testid="user-employment-type">{user.employment_type}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {user?.email?.charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-700">
                <DropdownMenuLabel className="text-white">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.email}</p>
                    <div className="flex gap-2 items-center">
                      <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
                      {user?.employment_type && (
                        <>
                          <span className="text-xs text-slate-600">•</span>
                          <p className="text-xs text-cyan-400 font-medium">{user.employment_type}</p>
                        </>
                      )}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-700" />
                {localStorage.getItem('isImpersonating') === 'true' && (
                  <>
                    <DropdownMenuItem onClick={handleStopImpersonating} className="text-orange-400 focus:bg-slate-800" data-testid="stop-impersonating-menu-item">
                      <LogIn className="w-4 h-4 mr-2" />
                      Stop Impersonating
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate('account-settings')} className="text-slate-300 focus:bg-slate-800 focus:text-white" data-testid="account-settings-menu-item">
                  <Settings className="w-4 h-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowChangePasswordDialog(true)} className="text-slate-300 focus:bg-slate-800 focus:text-white" data-testid="change-password-menu-item">
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={handleLogout} className="text-slate-300 focus:bg-slate-800 focus:text-white" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Impersonation Banner */}
      {localStorage.getItem('isImpersonating') === 'true' && (
        <div className="bg-orange-500/20 border-b border-orange-500/30 px-4 py-2">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-400" />
              <span className="text-sm font-medium text-orange-200">
                Impersonating: <span className="font-bold">{user?.email}</span>
              </span>
              <span className="text-xs text-orange-300">
                (Original: {localStorage.getItem('originalAdminEmail')})
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleStopImpersonating}
              className="bg-slate-900 hover:bg-slate-800 border-orange-500/50 text-orange-300"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Stop Impersonating
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-x-hidden">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-16 left-0 w-64 bg-slate-900/80 border-r border-slate-800 backdrop-blur-xl
            transform transition-transform duration-300 ease-in-out z-30
            lg:h-[calc(100vh-4rem)] h-[calc(100vh-4rem)]
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          data-testid="sidebar"
        >
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium
                    transition-all duration-200
                    ${isActive
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replaceAll(' ', '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col min-h-[calc(100vh-4rem)]">
          {/* Content - grows to fill space */}
          <div className="flex-1 p-4 lg:p-8 pt-6 md:pt-8 pb-8">
            <Routes>
              {user?.role === 'super_admin' ? (
                <Route path="/" element={<AdminDashboard user={user} />} />
              ) : (
                <Route path="/" element={<Home user={user} />} />
              )}
              <Route path="/customers" element={<CustomerList user={user} />} />
              <Route path="/income" element={<IncomePanel user={user} />} />
              <Route path="/expenses" element={<ExpensePanel user={user} />} />
              <Route path="/summary" element={<SummaryPanel user={user} />} />
              <Route path="/analytics" element={<Charts user={user} />} />
              <Route path="/checkbook" element={<Checkbook user={user} />} />
              <Route path="/mileage" element={<MileageTracker user={user} />} />
              {user?.role === 'owner' && (
                <Route path="/users" element={<UserManagement user={user} />} />
              )}
              {user?.role === 'super_admin' && (
                <Route path="/admin" element={<AdminDashboard user={user} />} />
              )}
              <Route path="account-settings" element={<AccountSettings user={user} />} />
            </Routes>
          </div>
          
          {/* Footer - fetched from settings */}
          <Footer variant="dark" />
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Change Password</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter your current password and choose a new password
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="oldPassword" className="text-slate-300">Current Password</Label>
              <Input
                id="oldPassword"
                type="password"
                value={passwordData.oldPassword}
                onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                placeholder="Enter current password"
                required
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-slate-300">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
                required
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Re-enter new password"
                required
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowChangePasswordDialog(false);
                setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
              }} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500">Change Password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
